
import React, { useRef } from 'react';
import Draggable, { DraggableData, DraggableEvent } from 'react-draggable';
import { DanceFloorConfig } from '../types';
import { LOGO_HBA_ICON_URL } from '../constants';
import { RotateIcon } from './icons';

interface DanceFloorItemProps {
  danceFloor: DanceFloorConfig;
  pixelScale: number;
  onDrag: (x: number, y: number) => void;
  onRotate: () => void;
  onSelect: () => void;
  isSelected: boolean;
  venueWidthPx: number;
  venueHeightPx: number;
  theme: 'light' | 'dark';
}

const DanceFloorItem: React.FC<DanceFloorItemProps> = ({ 
  danceFloor, pixelScale, onDrag, onRotate, onSelect, isSelected, venueWidthPx, venueHeightPx, theme
}) => {
  const draggableRef = useRef<HTMLDivElement>(null);

  if (!danceFloor.enabled || danceFloor.width === 0 || danceFloor.height === 0) return null;

  const contentWidthPx = danceFloor.width * pixelScale;
  const contentHeightPx = danceFloor.height * pixelScale;

  const draggableContainerWidthPx = danceFloor.orientation === 'vertical' ? contentHeightPx : contentWidthPx;
  const draggableContainerHeightPx = danceFloor.orientation === 'vertical' ? contentWidthPx : contentHeightPx;
  
  const position = {
    x: danceFloor.x * pixelScale - draggableContainerWidthPx / 2,
    y: danceFloor.y * pixelScale - draggableContainerHeightPx / 2,
  };
  
  const handleDrag = (e: DraggableEvent, data: DraggableData) => {
    onDrag((data.x + draggableContainerWidthPx / 2) / pixelScale, (data.y + draggableContainerHeightPx / 2) / pixelScale);
  };

  const bgColor = theme === 'dark' ? 'bg-hba-accent-purple/60' : 'bg-purple-400/60';
  const borderColor = theme === 'dark' ? 'border-hba-accent-pink' : 'border-purple-600';
  const selectionOutline = isSelected ? (theme === 'dark' ? 'outline outline-2 outline-offset-2 outline-yellow-400' : 'outline outline-2 outline-offset-2 outline-yellow-500') : '';

  const handleRotateClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onRotate();
  };

  return (
    <Draggable
      position={position}
      onStop={handleDrag} 
      bounds={{ left: 0, top: 0, right: venueWidthPx - draggableContainerWidthPx, bottom: venueHeightPx - draggableContainerHeightPx }}
      nodeRef={draggableRef} 
      cancel=".no-drag-df-action" 
    >
      <div
        ref={draggableRef}
        style={{
          width: `${draggableContainerWidthPx}px`,
          height: `${draggableContainerHeightPx}px`,
          position: 'absolute',
          touchAction: 'none',
        }}
        className={`cursor-grab group ${selectionOutline} z-20`} // Increased Z-index to be below controls but accessible
        onClick={(e) => { e.stopPropagation(); onSelect(); }}
      >
        {/* Inner div for rotation and content */}
        <div
          style={{
            width: `${contentWidthPx}px`, 
            height: `${contentHeightPx}px`,
            transform: `translate(-50%, -50%) ${danceFloor.orientation === 'vertical' ? 'rotate(90deg)' : 'rotate(0deg)'}`,
            transformOrigin: 'center center',
            position: 'absolute',
            top: '50%',
            left: '50%',
          }}
           className={`flex flex-col items-center justify-center p-1 ${bgColor} border ${borderColor} shadow-lg w-full h-full`}
        >
          {/* Added pointer-events-none so clicks pass through to the draggable container */}
          <div className="flex flex-col items-center text-center leading-tight w-full h-full justify-center pointer-events-none select-none"> 
            <span className={`text-xs md:text-sm font-medium ${theme === 'dark' ? 'text-hba-text-primary' : 'text-white'}`}>
              Pista
            </span>
            <span className={`text-xs md:text-sm font-medium ${theme === 'dark' ? 'text-hba-text-primary' : 'text-white'}`}>
              {danceFloor.width.toFixed(1)}m x {danceFloor.height.toFixed(1)}m
            </span>
            {/* Added draggable=false to prevent native ghost image dragging */}
            <img 
              src={LOGO_HBA_ICON_URL} 
              alt="HBA" 
              draggable="false"
              className="no-drag-df-action object-contain mt-1 opacity-80 select-none" 
              style={{ 
                  maxHeight: `${Math.min(contentHeightPx * 0.4, 80)}px`, 
                  maxWidth: '80%' 
              }} 
            />
          </div>
        </div>
        
        {isSelected && (
          <button
              onClick={handleRotateClick}
              className={`no-drag-df-action absolute -bottom-2.5 -right-2.5 p-1.5 rounded-full ${theme === 'dark' ? 'bg-hba-mid-purple text-hba-accent-pink' : 'bg-white text-purple-600'} shadow-lg hover:bg-opacity-80 z-30`}
              aria-label="Rotar Pista de Baile"
          >
              <RotateIcon className="w-4 h-4 md:w-5 sm:h-5" />
          </button>
        )}
      </div>
    </Draggable>
  );
};

export default DanceFloorItem;
